import React, {
  useState, useCallback, useEffect, useMemo, useRef,
} from 'react';
import Cookies from 'js-cookie';

import type { WithChildren } from 'common/types';
import { ISelectedFieldData, UnitedFieldState } from 'common/types/fields';

import { useTranslation } from 'react-i18next';
import type { ISchemaDataStorage, ISchemaDataStorageOrder, ISchemaDataStorageFields } from './types';
import { BeforeFieldsUpdate, FieldsDataHandlerContext, PredefinedContextType } from './context';

import { useFieldsUpdateHandler, useWholeSchemaRequest } from './api';
import { formatSchemaDataToStore, mergeUpdatedFieldsWithCurrentState } from './utils';
import useSearchQuery from '../../utils/useSearchQuery';
import recoverSessionId from '../../utils/recoverSessionId';


export const GenericFieldsHandlingLayer = ({ children }: WithChildren) => {
  const { i18n } = useTranslation();
  const searchParams = useSearchQuery();
  const [order, setOrder] = useState<ISchemaDataStorageOrder<string>>([]);
  const [fieldsDataSchema, setFieldsDataSchema] = useState<ISchemaDataStorageFields<string>>({});
  const [session, setSession] = useState<PredefinedContextType['session']>({ sessionId: recoverSessionId() });
  const [updatedFields, setUpdatedFields] = useState<Array<ISelectedFieldData>>();
  const [isInitializing, setIsInitializing] = useState<boolean>(true);

  // Optional pre-update hook (see registerBeforeFieldsUpdate). When it returns a
  // replacement sessionId, the update is dispatched to that session instead.
  const beforeUpdateRef = useRef<BeforeFieldsUpdate | null>(null);
  // While a session swap is in flight the whole-schema query refires for the new
  // key; its response must not reset field state (the clone state is identical,
  // and a late response would overwrite fresher field updates).
  const skipSchemaResetRef = useRef(false);

  // Concurrent updates can resolve with the same replacement id; apply it once
  // so the schema-reset skip is not left armed for a later legit load.
  const appliedSwapRef = useRef<string | null>(null);

  const registerBeforeFieldsUpdate = useCallback((interceptor: BeforeFieldsUpdate | null) => {
    beforeUpdateRef.current = interceptor;
  }, []);

  const applySessionSwap = useCallback((newSessionId: string) => {
    if (appliedSwapRef.current === newSessionId) return;
    appliedSwapRef.current = newSessionId;
    skipSchemaResetRef.current = true;
    Cookies.set('sessionId', newSessionId, { sameSite: 'lax' });
    setSession({ sessionId: newSessionId });
  }, []);

  const updateFields = useCallback((formUpdatedFields: Array<ISelectedFieldData>) => {
    const interceptor = beforeUpdateRef.current;
    if (interceptor) {
      interceptor()
        .then((newSessionId) => {
          if (newSessionId) applySessionSwap(newSessionId);
        })
        .catch(() => { /* keep the current session */ })
        .finally(() => setUpdatedFields(formUpdatedFields));
    } else {
      setUpdatedFields(formUpdatedFields);
    }
    console.log('Update Initialized', formUpdatedFields); // eslint-disable-line no-console
  }, [applySessionSwap]);

  const handleFieldsUpdate = useCallback((serverUpdatedFields: Array<UnitedFieldState>) => {
    const updatedState = mergeUpdatedFieldsWithCurrentState(fieldsDataSchema, serverUpdatedFields);
    setFieldsDataSchema(updatedState);
    console.log('Update finished', updatedState); // eslint-disable-line no-console
  }, [setFieldsDataSchema, fieldsDataSchema]);

  const getFieldsByName = useCallback((fieldsNames: Array<string>) => (
    fieldsNames.reduce((acc, fieldKey) => ({
      ...acc,
      [fieldKey]: fieldsDataSchema[fieldKey],
    }), {} as ISchemaDataStorage<string>['fields'])
  ), [fieldsDataSchema]);

  const {
    isFetching: isSchemaFetching,
    isLoading: isSchemaLoading,
    parsedError: schemaParsedError,
    data: initialSchemaWithData,
  } = useWholeSchemaRequest(session?.sessionId, searchParams);

  const {
    isFetching: isStateFetching,
    isLoading: isStateLoading,
    parsedError: stateParsedError,
  } = useFieldsUpdateHandler(updatedFields, session?.sessionId, handleFieldsUpdate);

  useEffect(() => {
    if (initialSchemaWithData && !isSchemaFetching && !isSchemaLoading) {
      if (skipSchemaResetRef.current) {
        // Refetch caused by a session swap: local state already matches the
        // cloned session, and resetting here could overwrite fresher updates.
        skipSchemaResetRef.current = false;
      } else {
        const parsedState = formatSchemaDataToStore(initialSchemaWithData.state, initialSchemaWithData.schema);
        setOrder(parsedState.order);
        setFieldsDataSchema(parsedState.fields);
        setIsInitializing(false);

        if (initialSchemaWithData.i18n) {
          console.log('I18n', initialSchemaWithData.i18n);
          i18n.addResourceBundle('en', 'translation', initialSchemaWithData.i18n, true, true);
          i18n.changeLanguage('en'); // This forces top level CSS update
        }
      }
    }
    if (initialSchemaWithData?.session.sessionId) {
      if (initialSchemaWithData.session.sessionId !== session.sessionId) {
        skipSchemaResetRef.current = true;
      }
      setSession({
        sessionId: initialSchemaWithData?.session.sessionId,
      });
      Cookies.set(
        'sessionId',
        initialSchemaWithData?.session.sessionId,
        {
          sameSite: 'lax',
        },
      );
    }
  }, [isSchemaFetching, isSchemaLoading, schemaParsedError, initialSchemaWithData]);

  const contextValue = useMemo<PredefinedContextType>(() => ({
    data: {
      order,
      fields: fieldsDataSchema,
      error: schemaParsedError || stateParsedError,
      isLoading: isSchemaLoading || isSchemaFetching || isStateLoading || isStateFetching || isInitializing,
    },
    session,
    actions: {
      updateFieldsState: updateFields,
      getFieldsByName,
      registerBeforeFieldsUpdate,
    },
  }), [
    order,
    fieldsDataSchema,
    schemaParsedError,
    stateParsedError,
    isSchemaLoading,
    isSchemaFetching,
    isStateLoading,
    isStateFetching,
    session,
    updateFields,
    getFieldsByName,
    registerBeforeFieldsUpdate,
  ]);

  return (
    <FieldsDataHandlerContext.Provider value={contextValue}>
      {children}
    </FieldsDataHandlerContext.Provider>
  );
};
